import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle, ArrowRight, ArrowLeft, Target, Star,
  Linkedin, Play, Mail, Zap, BookOpen,
  Briefcase, TrendingUp, Shield, Gift, X, Check, Minus
} from 'lucide-react';
import { motion } from 'framer-motion';
import { SITE_CONFIG } from '../../config/constants';
import SEO from '../../components/seo/SEO';

const testimonials = [
  {
    name: 'Camila Cardenal',
    achievement: 'De pastelera a AI Engineer en 4 meses',
    role: 'AI Engineer Jr — Trabajo remoto en USD para US',
    quote: '"De trabajar en pesos en Argentina a trabajar en dólares para US. Tu futuro se construye con las decisiones que hoy te animás a tomar. De pastelera a AI Engineer: con trabajo, dedicación y constancia… todo se puede."',
    time: '4 meses',
    result: 'AI Engineer Jr remoto',
    company: 'Trabajo en USD para US',
    videoUrl: 'https://www.linkedin.com/posts/camila-cardenal-451808181_nuevocomienzo-aiengineer-cleartech-activity-7413927672034000896-KAtl',
    linkedinUrl: 'https://www.linkedin.com/in/camila-cardenal-451808181/',
  },
  {
    name: 'Matías B.',
    achievement: '+50% de sueldo en mano',
    role: 'Mejoró comunicación y negoció mejor',
    quote: '"Con las mentorías mejoré mi habilidad de comunicación y entendí mi valor. Conseguí un 50% más en mano."',
    time: 'Con mentorías',
    result: '+50% en mano',
    company: 'Empresa para US',
    videoUrl: 'https://www.tiktok.com/@lucianoargolo0/video/7608273245288107271',
    linkedinUrl: '',
  },
  {
    name: 'Mauro Cortellezzi',
    achievement: 'Data Engineer en 2 semanas',
    role: 'Data Engineer en Tekton Labs',
    quote: '"Conseguí trabajo en Tekton Labs gracias a tus mentorías. Hice los cambios de LinkedIn el lunes, el miércoles ya me llegaron propuestas. Lo que hablamos en las entrevistas me recontra sirvió, usé todo lo que me dijiste. Las preguntas de SQL las saqué de una. Me ayudaste un montón."',
    time: '1 semana',
    result: 'Múltiples ofertas',
    company: 'Tekton Labs',
    videoUrl: 'https://www.linkedin.com/posts/luciano-argolo_felicidades-mauro-cortellezzi-por-tu-nuevo-activity-7392584149288546305-11F_',
    linkedinUrl: 'https://www.linkedin.com/in/mauro-cortellezzi-18112281/',
  },
  {
    name: 'Ramiro Crespi',
    achievement: 'Primer trabajo como Data Engineer a los 20 años',
    role: 'Jr Data Engineer',
    quote: '"Me ayudó a definir bien mis prioridades y entender por dónde ir. Pero lo más importante fue que me dio la confianza para estar cómodo en las entrevistas. A los 2 meses conseguí mi primer trabajo. Muy agradecido!"',
    time: '2 meses',
    result: 'Primer trabajo tech',
    company: 'Data Engineer',
    videoUrl: 'https://www.linkedin.com/feed/update/urn:li:ugcPost:7384564293033340928',
    linkedinUrl: 'https://www.linkedin.com/in/ramiro-crespi',
  },
  {
    name: 'Fernando Benítez',
    achievement: 'Mejoró su comunicación técnica con entrevistadores',
    role: 'Data Engineer',
    quote: '"La asesoría superó mis expectativas. Me hiciste ver las cosas desde la forma de negocio, hablando con KPIs, resumiendo todo de forma más digerible para un entrevistador. Me dejó todo mucho más claro."',
    time: '1 mes',
    result: 'Mejor comunicación técnica',
    company: 'Data Engineer',
    videoUrl: '',
    linkedinUrl: '',
  },
  {
    name: 'Federico Falcón',
    achievement: 'Encontró guía y objetivos claros para su carrera',
    role: 'Estudiante de Data Engineering',
    quote: '"Con tus mentorías encontré una guía para estudiar y la motivación para estar enfocado con objetivos claros y en busca de metas fijas. La verdad que me sirvieron mucho!"',
    time: 'En proceso',
    result: 'Roadmap definido',
    company: 'Estudiante activo',
    videoUrl: '',
    linkedinUrl: 'https://www.linkedin.com/in/federico-falcon-218a70336/',
  },
];

const valueBreakdown = [
  {
    icon: BookOpen,
    label: 'Bootcamp Databricks SQL completo',
    description: '10 semanas de contenido, ejercicios y proyecto final',
    value: 495,
  },
  {
    icon: Briefcase,
    label: 'Preparación para entrevistas técnicas',
    description: 'Simulacros reales con las preguntas que hacen las empresas',
    value: 200,
  },
  {
    icon: Linkedin,
    label: 'Optimización completa de LinkedIn',
    description: 'Perfil que atrae reclutadores y genera oportunidades 24/7',
    value: 150,
  },
  {
    icon: Target,
    label: 'Roadmap personalizado de carrera',
    description: 'Plan concreto con hitos, tecnologías y timeline',
    value: 200,
  },
  {
    icon: Zap,
    label: 'Acceso a plataforma de práctica',
    description: 'SQL, Python, Data Engineering — ejercicios interactivos y material exclusivo',
    value: 100,
  },
];

const faqs = [
  {
    q: '¿Cómo funciona? ¿Tengo que comprar un paquete directamente?',
    a: 'No. Empezás con una Sesión Diagnóstico ($100 USD). Ahí analizamos tu situación, definimos objetivos y te recomiendo el plan que mejor te sirve. Sin compromiso. Si después querés avanzar, elegís un plan mensual.',
  },
  {
    q: '¿Cómo funcionan los planes mensuales?',
    a: 'Cada mes incluye 4 sesiones (1 por semana). Podés contratar 1, 2 o 3 meses. Cuantos más meses contratás, mejor precio por sesión y más beneficios: $1,000 (1 mes), $1,900 (2 meses) o $2,700 (3 meses). Los planes de 2 y 3 meses incluyen el Bootcamp completo.',
  },
  {
    q: '¿Necesito experiencia previa en Data Engineering?',
    a: 'No. Las mentorías se adaptan a tu nivel. Si venís de analytics, desarrollo, BI o incluso otra industria, armamos un plan desde donde estés.',
  },
  {
    q: '¿Qué pasa si no puedo asistir a una sesión?',
    a: 'Podés reprogramar hasta 2 veces por plan, con un mínimo de 48 horas de anticipación. Después de 2 reprogramaciones, la sesión se cuenta como tomada.',
  },
  {
    q: '¿Las sesiones tienen vencimiento?',
    a: 'Sí. Cada mes de tu plan tiene 4 semanas para usar las 4 sesiones correspondientes. Las sesiones no utilizadas dentro del plazo no se reembolsan ni transfieren.',
  },
  {
    q: '¿Incluye acceso a contenido técnico y ejercicios?',
    a: 'Sí. Todos los planes incluyen acceso a la plataforma de desarrollo con ejercicios. Los planes de 2 y 3 meses además incluyen acceso completo al Bootcamp de Databricks SQL ($495 USD de valor).',
  },
  {
    q: '¿Cuánto dura cada sesión?',
    a: '60 minutos. Cada sesión es 100% enfocada en tus objetivos, no en contenido genérico.',
  },
  {
    q: '¿Puedo cancelar o pedir reembolso?',
    a: 'La Sesión Diagnóstico no tiene reembolso. Para planes mensuales, si no iniciaste ninguna sesión podés solicitar reembolso dentro de los 7 días posteriores a la compra. Una vez iniciado el plan, las sesiones restantes siguen sujetas al plazo de vencimiento.',
  },
  {
    q: '¿Para quién es esta mentoría?',
    a: 'Para cualquier persona que quiera entrar o crecer en Data Engineering: analistas que quieren dar el salto, devs en transición, juniors que quieren acelerar su carrera, o profesionales de otras industrias que quieren hacer el cambio.',
  },
];

export default function Mentorias() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalValue = valueBreakdown.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <SEO
        title="Mentorías 1:1 en Data Engineering | Luciano Argolo"
        description="Mentorías personalizadas 1:1 en Data Engineering. Sesión Diagnóstico $100 USD. Planes desde $1,000/mes (4 sesiones semanales). Planes de 2 y 3 meses incluyen Bootcamp de Databricks SQL. Preparación para entrevistas y roadmap de carrera."
        keywords="mentoría data engineering, mentoría personalizada, data engineer, carrera data engineering, databricks, entrevistas técnicas"
        url="/mentorias"
      />
      <div className="min-h-screen bg-white">
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/20 mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al inicio
            </Button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-3xl mx-auto"
            >
              <Badge className="bg-green-600 text-white text-sm mb-6">Cupos limitados</Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Tu carrera en Data Engineering,{' '}
                <span className="text-green-400">con dirección</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Un mentor que ya recorrió el camino, que te dice exactamente
                qué hacer y te acompaña en cada paso.
              </p>
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
                onClick={() => document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Ver planes y precios
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Qué problema resuelve */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ¿Te suena familiar?
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Estudiás por tu cuenta pero no sabés si vas por buen camino',
                'Mandás CVs y no te llaman, o te rechazan en la técnica',
                'Sabés SQL y Python pero no sabés cómo armar un portfolio que demuestre tu nivel',
                'Ves ofertas de Data Engineer pero no sabés qué te falta para aplicar',
                'Tu LinkedIn no genera oportunidades, solo silencio',
                'Querés trabajar remoto para afuera pero no sabés cómo dar el salto',
              ].map((pain, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3 p-4 bg-red-50 rounded-xl border border-red-100"
                >
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-500 text-sm font-bold">✕</span>
                  </div>
                  <p className="text-gray-700">{pain}</p>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <p className="text-xl text-gray-600">
                La diferencia entre alguien que lo logra y alguien que sigue dando vueltas
                no es talento. Es <span className="font-bold text-gray-900">tener a alguien que te diga exactamente qué hacer</span>.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Quién soy y por qué puedo ayudarte */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Tu mentor
              </h2>
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">Luciano Argolo</h3>
                    <p className="text-green-600 font-semibold mb-4">Sr Data Engineer</p>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        Fui radiólogo. Hice la transición completa a Data Engineering y hoy trabajo
                        remoto para empresas de US. No vengo de una familia tech ni tuve contactos en la industria.
                      </p>
                      <p>
                        Lo que sí tuve fue un camino lleno de errores que hoy puedo ahorrarte.
                        Sé exactamente qué se necesita porque lo viví: las entrevistas, las dudas sobre
                        qué aprender primero, la frustración de no saber si vas por buen camino.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-6">
                      {[
                        'Databricks', 'SQL Avanzado', 'Python', 'Spark',
                        'Airflow', 'AWS', 'Data Modeling'
                      ].map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-200"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-4 mt-6">
                      <a
                        href={SITE_CONFIG.social.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonios */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Resultados reales
                </h2>
                <p className="text-lg text-gray-600">
                  No promesas. Personas reales que transformaron su carrera.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.08 }}
                    viewport={{ once: true }}
                  >
                    <Card className="bg-white border-2 border-gray-200 hover:border-green-500 transition-all duration-300 h-full flex flex-col">
                      <CardContent className="p-6 flex-grow flex flex-col">
                        <div className="flex justify-center gap-1 mb-3">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        {testimonial.achievement && (
                          <p className="text-center font-bold text-green-700 text-sm mb-3 bg-green-50 rounded-lg py-2 px-3 border border-green-200">
                            {testimonial.achievement}
                          </p>
                        )}
                        <p className="text-gray-700 italic mb-4 flex-grow text-sm leading-relaxed">
                          {testimonial.quote}
                        </p>
                        <div className="border-t border-gray-200 pt-4 mt-auto">
                          <p className="font-bold text-gray-900 mb-1">{testimonial.name}</p>
                          <p className="text-sm text-gray-500 mb-3">{testimonial.role}</p>
                          <div className="flex flex-wrap gap-2">
                            {testimonial.videoUrl && (
                              <a
                                href={testimonial.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm hover:shadow-md"
                              >
                                <Play className="w-3 h-3" />
                                Ver video
                              </a>
                            )}
                            {testimonial.linkedinUrl && (
                              <a
                                href={testimonial.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-blue-600 px-3 py-1.5 rounded-full text-xs font-medium transition-all border border-blue-300"
                              >
                                <Linkedin className="w-3 h-3" />
                                LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Desglose de valor */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Esto es lo que incluye tu mentoría
                </h2>
                <p className="text-lg text-gray-600">
                  No son solo sesiones. Es un sistema completo para acelerar tu carrera.
                </p>
              </div>

              <div className="space-y-4 mb-12">
                {valueBreakdown.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm"
                  >
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-gray-400 line-through text-sm">${item.value} USD</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white text-center"
              >
                <p className="text-lg mb-2">Si contratases cada servicio por separado, estarías pagando</p>
                <p className="text-4xl font-bold mb-4">
                  +${totalValue.toLocaleString()} USD
                </p>
                <p className="text-green-100 text-lg">
                  Con la mentoría, accedés a todo incluido.
                </p>
                <Button
                  size="lg"
                  className="mt-6 bg-white text-green-700 hover:bg-green-50 py-3 px-8 rounded-full font-bold shadow-lg hover:shadow-xl transition-all"
                  onClick={() => document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver los planes
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Qué incluyen las sesiones */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
                ¿Qué hacemos en las sesiones?
              </h2>
              <p className="text-lg text-gray-600 text-center mb-12">
                Cada sesión se adapta a lo que necesitás en ese momento
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: Target,
                    title: 'Roadmap de carrera',
                    desc: 'Definimos tu objetivo, analizamos tu situación actual y creamos un plan concreto con hitos y plazos.',
                  },
                  {
                    icon: Briefcase,
                    title: 'Simulacros de entrevistas',
                    desc: 'Preguntas reales de SQL, Python, diseño de sistemas y behavioral. Feedback directo para mejorar.',
                  },
                  {
                    icon: Linkedin,
                    title: 'LinkedIn y marca personal',
                    desc: 'Optimizamos tu perfil para que los reclutadores te encuentren. Headline, about, experiencia, todo.',
                  },
                  {
                    icon: BookOpen,
                    title: 'Revisión de código y proyectos',
                    desc: 'Revisamos tu portfolio, tus pipelines, tus queries. Te doy feedback como lo haría un tech lead.',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Estrategia de búsqueda laboral',
                    desc: 'Dónde aplicar, cómo negociar salario, cómo manejar contraofertas y procesos con empresas de afuera.',
                  },
                  {
                    icon: Shield,
                    title: 'Resolución de dudas técnicas',
                    desc: 'Databricks, SQL, modelado, arquitectura, cloud. Lo que necesites para destrabar tu aprendizaje.',
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    viewport={{ once: true }}
                  >
                    <Card className="border-2 border-gray-100 hover:border-green-200 transition-all h-full">
                      <CardContent className="p-6">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                          <item.icon className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Cómo funciona */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
                ¿Cómo funciona?
              </h2>
              <p className="text-lg text-gray-600 text-center mb-12">
                Tres pasos. Sin letra chica.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    step: '1',
                    title: 'Sesión Diagnóstico',
                    desc: 'Analizamos tu situación, definimos objetivos y te recomiendo el camino más corto. Sin compromiso.',
                    highlight: '$100 USD',
                  },
                  {
                    step: '2',
                    title: 'Elegís tu plan',
                    desc: 'Si tiene sentido avanzar, elegís cuántos meses querés trabajar juntos. Más meses, mejor precio.',
                    highlight: '1, 2 o 3 meses',
                  },
                  {
                    step: '3',
                    title: 'Ejecutamos',
                    desc: '4 sesiones por mes, 1 por semana. Plan concreto, cadencia real y resultados medibles.',
                    highlight: 'Desde $1,000/mes',
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 bg-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{item.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 mb-3">{item.desc}</p>
                    <span className="inline-block bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-semibold border border-green-200">
                      {item.highlight}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Planes */}
        <section id="planes" className="py-16 bg-gray-900">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Elegí tu plan
              </h2>
              <p className="text-lg text-gray-400">
                Empezá con la Sesión Diagnóstico. Después elegí el paquete que mejor te sirva.
              </p>
            </div>

            {/* Diagnostic Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-md mx-auto mb-12"
            >
              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/40 shadow-xl">
                <CardHeader className="text-center pb-4">
                  <Badge className="bg-amber-600 text-white text-xs mb-3">PRIMER PASO</Badge>
                  <CardTitle className="text-2xl font-bold text-white mb-2">
                    {SITE_CONFIG.mentoring.diagnostic.name}
                  </CardTitle>
                  <div className="text-4xl font-bold text-amber-400">
                    ${SITE_CONFIG.mentoring.diagnostic.totalPrice.toFixed(0)} USD
                  </div>
                  <p className="text-sm text-gray-300 mt-3">
                    {SITE_CONFIG.mentoring.diagnostic.description}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-200">
                  {SITE_CONFIG.mentoring.diagnostic.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>

            {/* Arrow */}
            <div className="flex justify-center mb-8">
              <div className="flex flex-col items-center text-gray-500">
                <span className="text-sm mb-2">Después elegís</span>
                <ArrowRight className="w-6 h-6 rotate-90" />
              </div>
            </div>

            {/* Package Plans */}
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {SITE_CONFIG.mentoring.plans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  viewport={{ once: true }}
                >
                  <Card className={`bg-gradient-to-br from-gray-800 to-gray-900 border-2 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col ${
                    plan.badge === 'MEJOR VALOR' ? 'border-green-500 md:scale-105' : plan.badge === 'POPULAR' ? 'border-blue-500/40' : 'border-gray-600'
                  }`}>
                    <CardHeader className="text-center pb-4">
                      {plan.badge && (
                        <Badge className={`${
                          plan.badgeColor === 'green' ? 'bg-green-600' :
                          plan.badgeColor === 'blue' ? 'bg-blue-600' : 'bg-gray-600'
                        } text-white text-xs mb-3`}>
                          {plan.badge}
                        </Badge>
                      )}
                      <CardTitle className="text-2xl font-bold text-white mb-2">
                        {plan.name}
                      </CardTitle>
                      <div className="space-y-2">
                        {'monthlyPrice' in plan && typeof plan.monthlyPrice === 'number' && (
                          <div className="text-4xl font-bold text-green-400">
                            ${plan.monthlyPrice.toFixed(0)} <span className="text-lg font-normal text-gray-400">USD/mes</span>
                          </div>
                        )}
                        {'months' in plan && typeof plan.months === 'number' && plan.months > 1 && (
                          <p className="text-sm text-gray-300">
                            ${plan.totalPrice.toFixed(0)} USD total
                          </p>
                        )}
                        {plan.savings && (
                          <div className="inline-block bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                            {plan.savings}
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-300 mt-4">
                        {plan.sessions} sesiones de {SITE_CONFIG.mentoring.sessionDuration} min
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4 flex-grow flex flex-col">
                      <div className="space-y-2 text-gray-200 flex-grow">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Tabla comparativa */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mt-16 max-w-4xl mx-auto"
            >
              <h3 className="text-2xl font-bold text-white mb-8 text-center">
                Compará los planes
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-4 px-4 text-gray-400 font-medium w-2/5">Qué incluye</th>
                      <th className="text-center py-4 px-3 text-white font-semibold">1 Mes</th>
                      <th className="text-center py-4 px-3 text-blue-400 font-semibold">2 Meses</th>
                      <th className="text-center py-4 px-3 text-green-400 font-semibold">
                        3 Meses
                        <div className="text-[10px] bg-green-600 text-white rounded-full px-2 py-0.5 mt-1 inline-block">MEJOR VALOR</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {[
                      { feature: 'Sesiones 1:1 de 60 min', m1: '4', m2: '8', m3: '12' },
                      { feature: 'Precio por sesión', m1: '$250', m2: '$237', m3: '$225' },
                      { feature: 'Plataforma de desarrollo con ejercicios', m1: true, m2: true, m3: true },
                      { feature: 'Roadmap personalizado de carrera', m1: true, m2: true, m3: true },
                      { feature: 'Revisión de CV y portfolio', m1: true, m2: true, m3: true },
                      { feature: 'Soporte por chat entre sesiones', m1: true, m2: true, m3: true },
                      { feature: 'Optimización de perfil de LinkedIn', m1: false, m2: true, m3: true },
                      { feature: 'Preparación para entrevistas técnicas', m1: false, m2: true, m3: true },
                      { feature: 'Bootcamp Databricks SQL ($495 USD de valor)', m1: false, m2: true, m3: true, highlight: true },
                      { feature: 'Simulacros de entrevistas con feedback', m1: false, m2: false, m3: true },
                      { feature: 'Acompañamiento por WhatsApp ilimitado', m1: false, m2: false, m3: true },
                      { feature: 'Desarrollo de marca personal', m1: false, m2: false, m3: true },
                      { feature: 'Agenda de hábitos y plan de estudio', m1: false, m2: false, m3: true },
                    ].map((row, idx) => (
                      <tr
                        key={idx}
                        className={`border-b border-gray-800 ${row.highlight ? 'bg-green-500/10' : ''}`}
                      >
                        <td className={`py-3 px-4 ${row.highlight ? 'text-green-400 font-semibold' : ''}`}>
                          {row.feature}
                        </td>
                        {[row.m1, row.m2, row.m3].map((val, i) => (
                          <td key={i} className="text-center py-3 px-3">
                            {typeof val === 'string' ? (
                              <span className={`font-semibold ${i === 2 ? 'text-green-400' : 'text-white'}`}>{val}</span>
                            ) : val ? (
                              <Check className={`w-5 h-5 mx-auto ${i === 2 ? 'text-green-400' : 'text-blue-400'}`} />
                            ) : (
                              <Minus className="w-5 h-5 mx-auto text-gray-600" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-600">
                      <td className="py-4 px-4 text-white font-bold text-base">Total</td>
                      <td className="text-center py-4 px-3 text-white font-bold text-lg">$1,000</td>
                      <td className="text-center py-4 px-3 text-blue-400 font-bold text-lg">$1,900</td>
                      <td className="text-center py-4 px-3 text-green-400 font-bold text-lg">$2,700</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-center text-gray-500 text-xs mt-4">
                Los planes de 2 y 3 meses incluyen el Bootcamp de Databricks SQL ($495 USD de valor).
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              viewport={{ once: true }}
              className="flex flex-col items-center mt-12"
            >
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-lg font-semibold"
                onClick={() => window.open('https://calendly.com/lucianoagustinargolo/sesion_informativa', '_blank')}
              >
                Agendar Sesión Diagnóstico
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-gray-500 mt-4 text-sm">
                Sin compromiso. Si no es para vos, te lo digo.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Condiciones */}
        <section className="py-12 bg-gray-800">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-white mb-6 text-center">
                Condiciones de las mentorías
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Cada mes tiene 4 semanas para usar tus 4 sesiones. Las sesiones no utilizadas dentro del plazo no se reembolsan.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Podés reprogramar hasta 2 veces por plan con 48hs de anticipación. Después, la sesión se cuenta como tomada.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>El intervalo máximo entre sesiones es de 30 días. Mantener la cadencia es clave para los resultados.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>Reembolsos disponibles solo si no iniciaste ninguna sesión del plan, dentro de los 7 días posteriores a la compra.</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                Preguntas frecuentes
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.details
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="group bg-white rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 hover:text-green-600 transition-colors">
                      {faq.q}
                      <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-600">
                      {faq.a}
                    </div>
                  </motion.details>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 text-white">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Gift className="w-12 h-12 mx-auto mb-6 text-green-400" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Empezá con la Sesión Diagnóstico
              </h2>
              <p className="text-xl text-gray-300 mb-4">
                60 minutos para analizar tu situación, definir objetivos y armar un plan concreto.
                Si no es para vos, te lo digo.
              </p>
              <p className="text-gray-400 mb-8">
                Prefiero ser honesto a venderte algo que no necesitás.
              </p>
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all text-lg"
                onClick={() => window.open('https://calendly.com/lucianoagustinargolo/sesion_informativa', '_blank')}
              >
                Agendar Sesión Diagnóstico
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
